import {
  Button,
  PocketRow,
  PocketSection,
  PocketStatement,
} from "@/components/ui";
import { useNavigate } from "react-router-dom";
import { openMemoryNode } from "@/components/layout/memory-node-navigation";
import { useCanvasStore } from "@/stores/canvas-store";
import { useProjectStore } from "@/stores/project-store";
import { captureDesktopChatAuthScopeGuard } from "@/desktop/desktop-chat-auth-scope";
import { Icons } from "@/components/ui/icons";
import { useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { settingsMemoryMessages } from "@/i18n/messages/settings-memory";
import { useSettingsNav } from "./settings-navigation";
import { useMemoryReview } from "./memory-review-state";
import { MemorySettingsRows } from "./memory-settings-rows";
import { SettingsFieldError } from "./settings-text-field";
import { useSubscriptionStatus } from "./account-plan-state";

export function MemorySection() {
  const t = useT(settingsMemoryMessages);
  const tc = useT(commonMessages);
  const nav = useSettingsNav();
  const navigate = useNavigate();
  const { status } = useSubscriptionStatus();
  const review = useMemoryReview(status?.has_pro_access === true);
  const count = review.pending?.total ?? review.pending?.items.length ?? null;
  return (
    <div data-testid="memory-section">
      <PocketStatement
        size="panel"
        title={
          count === null
            ? t("loadingMemory")
            : count === 0
              ? t("nothingToReview")
              : t("needsReview", { count })
        }
      />
      <SettingsFieldError error={review.error} testId="memory-review-error" />
      {review.error ? (
        <Button
          variant="ghost"
          size="sm"
          data-testid="memory-review-retry"
          disabled={review.busy}
          onClick={() => void review.reload()}
        >
          {tc("retry")}
        </Button>
      ) : null}
      {count ? (
        <PocketSection
          title={t("toReview")}
          action={
            count ? (
              <Button
                size="sm"
                variant="secondary"
                disabled={review.busy}
                data-testid="memory-accept-all"
                onClick={() =>
                  void review.decide(
                    review.pending?.items ?? [],
                    "accept",
                    true,
                  )
                }
              >
                {t("acceptAll")}
              </Button>
            ) : undefined
          }
        >
          {review.pending?.items.map((item) => (
            <PocketRow
              key={item.id}
              testId={`memory-review-${item.id}`}
              title={item.title}
              secondLine={item.reason_label}
              chevron
              hoverActions={
                <>
                  <Button
                    size="iconXs"
                    variant="ghost"
                    aria-label={t("acceptMemory")}
                    disabled={review.busy}
                    data-testid={`memory-accept-${item.id}`}
                    onClick={() => void review.decide([item], "accept")}
                  >
                    <Icons.Check className="h-3 w-3" />
                  </Button>
                  <Button
                    size="iconXs"
                    variant="ghost"
                    aria-label={t("rejectMemory")}
                    disabled={review.busy}
                    data-testid={`memory-reject-${item.id}`}
                    onClick={() => void review.decide([item], "reject")}
                  >
                    <Icons.X className="h-3 w-3" />
                  </Button>
                </>
              }
              onSelect={() =>
                nav.push({
                  page: "memory-proposal",
                  args: { reviewId: item.id, title: item.title },
                })
              }
            />
          ))}
          {review.pending?.pagination.has_more ? (
            <PocketRow
              title={t("showMore")}
              testId="memory-review-more"
              disabled={review.busy}
              onSelect={() => void review.loadMore()}
            />
          ) : null}
        </PocketSection>
      ) : null}
      {review.recent.length ? (
        <PocketSection title={t("recentDecisions")}>
          {review.recent.map((item) => (
            <PocketRow
              key={item.id}
              testId={`memory-recent-${item.id}`}
              title={item.title}
              secondLine={item.reason_label}
              trailing={
                item.undo_available ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={review.busy}
                    data-testid={`memory-undo-${item.id}`}
                    onClick={() => void review.decide([item], "undo")}
                  >
                    {t("undo")}
                  </Button>
                ) : undefined
              }
            />
          ))}
        </PocketSection>
      ) : null}
      <MemorySettingsRows
        onOpenGraph={() => {
          const scope = captureDesktopChatAuthScopeGuard();
          nav.close();
          navigate("/");
          window.setTimeout(() => {
            if (!scope()) return;
            const { currentProject, currentSpace } = useProjectStore.getState();
            openMemoryNode({
              nodeId: null,
              currentProject,
              currentSpace,
              selectFrame: useCanvasStore.getState().selectFrame,
            });
          }, 200);
        }}
      />
    </div>
  );
}
